'use client';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';
import clsx from 'clsx';
import { flushSync } from 'react-dom';
import { useRouter } from 'next/navigation';

import Button from '@/components/ui/button';
import { pluralize } from '@/utils/pluralize';

/** Viewport max-width matching Tailwind `lg` (1024px). */
const MOBILE_LISTING_MEDIA = '(max-width: 1023px)';

/**
 * Scroll-padding and bottom padding on the document root so `scrollIntoView`
 * and scroll targets clear the fixed mobile bar when the window is the scrollport.
 */
const LISTING_STICKY_SCROLL_PADDING_BOTTOM =
  'calc(5.5rem + env(safe-area-inset-bottom, 0px))';

/**
 * Extra scrollable space so footer clears the fixed CTA — ~bar height + small buffer.
 */
const LISTING_STICKY_SCROLL_CONTENT_PAD_BOTTOM =
  'calc(4.75rem + env(safe-area-inset-bottom, 0px))';

const ReviewStarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 21 21"
    className="h-3.5 w-3.5 shrink-0 text-gray-900"
    aria-hidden
  >
    <path
      d="M20.9702 8.25086C20.9702 7.93995 20.7351 7.74675 20.2642 7.67111L13.9381 6.75121L11.1027 1.01713C10.9432 0.67265 10.7371 0.500488 10.4852 0.500488C10.2332 0.500488 10.0273 0.67265 9.86767 1.01713L7.03203 6.75121L0.705662 7.67111C0.235272 7.74675 0 7.93995 0 8.25086C0 8.42735 0.105029 8.62906 0.315088 8.85583L4.90236 13.317L3.81865 19.6182C3.80179 19.7359 3.79344 19.82 3.79344 19.8703C3.79344 20.0468 3.83752 20.1958 3.92569 20.3178C4.01386 20.4397 4.14611 20.5005 4.32261 20.5005C4.47389 20.5005 4.64187 20.4504 4.82672 20.3494L10.485 17.375L16.1438 20.3494C16.3204 20.4502 16.4884 20.5005 16.6477 20.5005C16.8162 20.5005 16.9441 20.4399 17.0323 20.3178C17.1203 20.1959 17.1644 20.0468 17.1644 19.8703C17.1644 19.7611 17.1602 19.6771 17.1517 19.6182L16.0678 13.317L20.6424 8.85583C20.8611 8.63741 20.9702 8.43571 20.9702 8.25086Z"
      fill="currentColor"
    />
  </svg>
);

export const LISTING_AVAILABILITY_ANCHOR_ID = 'listing-availability';

export type StickyBookingSummary = {
  /** Formatted currency for the stay (before taxes), e.g. "$441". */
  priceText?: string;
  /** Stay line after "for", e.g. "2 nights · Apr 3 – 5". */
  metaText: string;
  /** Optional second line (e.g. guest count) so the stay line stays short on narrow widths. */
  guestLine?: string;
};

export type StickyNoDatesPitch = {
  headline: string;
  rating?: {
    average: number;
    reviewCount: number;
  };
};

type ListingMobileAvailabilityAnchorProps = {
  children: React.ReactNode;
  stickyCtaLabel: string;
  stickyBookingSummary?: StickyBookingSummary;
  /** Shown when there are no dates selected (Check Availability). */
  stickyNoDatesPitch?: StickyNoDatesPitch;
  /** When set with `stickyBookingSummary`, Reserve navigates here instead of scrolling. */
  stickyReserveBookingHref?: string;
};

export const ListingMobileAvailabilityAnchor = ({
  children,
  stickyCtaLabel,
  stickyBookingSummary,
  stickyNoDatesPitch,
  stickyReserveBookingHref,
}: ListingMobileAvailabilityAnchorProps) => {
  const router = useRouter();
  const [isRoutePending, startTransition] = useTransition();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [reserveNavigating, setReserveNavigating] = useState(false);
  const reserveLoading = reserveNavigating || isRoutePending;

  const scrollToAvailability = useCallback(() => {
    const el = sentinelRef.current;
    if (!el) {
      return;
    }
    const instant = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    el.scrollIntoView?.({
      block: 'start',
      behavior: instant ? 'instant' : 'smooth',
    });
  }, []);

  const onReserveStickyClick = useCallback(() => {
    if (stickyBookingSummary && stickyReserveBookingHref) {
      if (reserveLoading) {
        return;
      }
      // router.push returns void — do not tie loading to a Promise or it clears
      // before React paints. flushSync commits loading state before navigation.
      flushSync(() => {
        setReserveNavigating(true);
      });
      startTransition(() => {
        router.push(stickyReserveBookingHref);
      });
      return;
    }
    scrollToAvailability();
  }, [
    reserveLoading,
    router,
    scrollToAvailability,
    stickyBookingSummary,
    stickyReserveBookingHref,
  ]);

  useEffect(() => {
    setReserveNavigating(false);
  }, [stickyReserveBookingHref]);

  useEffect(() => {
    if (!reserveNavigating) {
      return;
    }
    const id = window.setTimeout(() => {
      setReserveNavigating(false);
    }, 15000);
    return () => window.clearTimeout(id);
  }, [reserveNavigating]);

  useEffect(() => {
    const scrollRoot = document.documentElement;
    const mql = window.matchMedia(MOBILE_LISTING_MEDIA);
    const apply = () => {
      if (mql.matches) {
        scrollRoot.style.scrollPaddingBottom = LISTING_STICKY_SCROLL_PADDING_BOTTOM;
        scrollRoot.style.paddingBottom = LISTING_STICKY_SCROLL_CONTENT_PAD_BOTTOM;
      } else {
        scrollRoot.style.scrollPaddingBottom = '';
        scrollRoot.style.paddingBottom = '';
      }
    };
    apply();
    mql.addEventListener('change', apply);
    return () => {
      mql.removeEventListener('change', apply);
      scrollRoot.style.scrollPaddingBottom = '';
      scrollRoot.style.paddingBottom = '';
    };
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const mql = window.matchMedia(MOBILE_LISTING_MEDIA);
    let observer: IntersectionObserver | null = null;

    const attach = () => {
      observer?.disconnect();
      observer = null;
      if (!mql.matches) {
        setShowStickyBar(false);
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          setShowStickyBar(!entry.isIntersecting);
        },
        {
          root: null,
          threshold: 0,
          rootMargin: '0px 0px -12px 0px',
        }
      );
      observer.observe(el);
    };

    attach();
    mql.addEventListener('change', attach);

    return () => {
      mql.removeEventListener('change', attach);
      observer?.disconnect();
    };
  }, []);

  return (
    <>
      <div
        ref={sentinelRef}
        id={LISTING_AVAILABILITY_ANCHOR_ID}
        className="pb-[1rem] lg:hidden"
      >
        {children}
      </div>
      {showStickyBar ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-2 bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] lg:hidden animate-fade-in">
          {stickyBookingSummary ? (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                {stickyBookingSummary.priceText ? (
                  <p className="break-words text-base font-semibold leading-tight text-gray-900">
                    From{' '}
                    <span className="underline decoration-1 underline-offset-2">
                      {stickyBookingSummary.priceText}
                    </span>
                  </p>
                ) : null}
                <p
                  className={clsx(
                    'text-sm font-normal leading-snug text-gray-600 break-words',
                    stickyBookingSummary.guestLine && 'whitespace-pre-line',
                    stickyBookingSummary.priceText ? 'mt-0.5' : ''
                  )}
                >
                  {stickyBookingSummary.guestLine
                    ? `for ${stickyBookingSummary.metaText}\n${stickyBookingSummary.guestLine}`
                    : `for ${stickyBookingSummary.metaText}`}
                </p>
              </div>
              <Button
                type="button"
                normalCase
                radius="round"
                className="shrink-0 min-h-[2.75rem] min-w-[7.5rem] px-5 py-3 text-sm font-semibold [&_[role=status]_svg]:!text-white [&_[role=status]_svg_path]:!fill-current"
                disabled={reserveLoading}
                disabledCTA={reserveLoading}
                loading={reserveLoading}
                onClick={onReserveStickyClick}
              >
                {stickyCtaLabel}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="min-w-0 break-words text-base font-semibold leading-tight text-gray-900">
                  {stickyNoDatesPitch?.headline ?? 'Add dates for pricing'}
                </p>
                {stickyNoDatesPitch?.rating ? (
                  <p className="mt-0.5 flex min-w-0 items-center gap-1 truncate text-sm font-normal leading-tight text-gray-600">
                    <ReviewStarIcon />
                    <span className="shrink-0 font-semibold">
                      {stickyNoDatesPitch.rating.average.toFixed(1)}
                    </span>
                    <span className="shrink-0" aria-hidden>
                      ·
                    </span>
                    <span className="truncate text-gray-6">
                      {stickyNoDatesPitch.rating.reviewCount}{' '}
                      {pluralize(
                        'review',
                        stickyNoDatesPitch.rating.reviewCount
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                normalCase
                radius="round"
                className="shrink-0 px-5 py-3 text-sm font-semibold"
                onClick={scrollToAvailability}
              >
                {stickyCtaLabel}
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
};
